import { z } from 'zod';
import { idSchema } from './common';

export const taxDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value && Number(value.slice(0, 4)) >= 1;
}, 'Expected a real Gregorian date');

export const TAX_DATE_FIELDS = ['effectiveDate', 'issueDate', 'firstPeriodStartDate', 'firstPeriodEndDate', 'firstReturnDueDate'] as const;
export const taxReviewFieldsSchema = z.object({
  effectiveDate: taxDateSchema.nullable().optional(),
  issueDate: taxDateSchema.nullable().optional(),
  firstPeriodStartDate: taxDateSchema.nullable().optional(),
  firstPeriodEndDate: taxDateSchema.nullable().optional(),
  firstReturnDueDate: taxDateSchema.nullable().optional(),
  taxPeriodPattern: z.string().trim().max(200).nullable().optional(),
}).strict().refine((value) => !value.firstPeriodStartDate || !value.firstPeriodEndDate || value.firstPeriodStartDate <= value.firstPeriodEndDate, 'Tax period start must not follow its end');
export type TaxReviewFields = z.infer<typeof taxReviewFieldsSchema>;
export type TaxKind = 'VAT' | 'CT';

export function taxKindForService(serviceType: string, jurisdiction: string | null | undefined): TaxKind | null {
  if (jurisdiction !== 'UAE') return null;
  return serviceType === 'VAT Return Filing' ? 'VAT' : serviceType === 'Corporate Tax Return Filing' ? 'CT' : null;
}

export function taxKindForDocument(type: string): TaxKind | null {
  return type === 'VAT_CERTIFICATE' ? 'VAT' : type === 'CORPORATE_TAX_CERTIFICATE' ? 'CT' : null;
}

export function taxDate(value: unknown): string | null {
  const parsed = taxDateSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function taxUtc(year: number, month: number, day: number): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month, day); date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

export function taxDeadline(kind: TaxKind, end: string): string {
  const date = new Date(`${taxDateSchema.parse(end)}T00:00:00.000Z`);
  if (kind === 'VAT') date.setUTCDate(date.getUTCDate() + 28);
  else {
    const year = date.getUTCFullYear(), month = date.getUTCMonth() + 9, day = date.getUTCDate();
    date.setUTCDate(1); date.setUTCMonth(month);
    date.setUTCDate(Math.min(day, new Date(taxUtc(year, month + 1, 0)).getUTCDate()));
  }
  return taxDateSchema.parse(date.toISOString().slice(0, 10));
}

export function taxEvidenceFields(kind: TaxKind, extracted: Record<string, unknown> | null | undefined) {
  const firstPeriodStartDate = taxDate(extracted?.firstPeriodStartDate), firstPeriodEndDate = taxDate(extracted?.firstPeriodEndDate);
  const firstReturnDueDate = taxDate(extracted?.firstReturnDueDate);
  const validPeriod = !!firstPeriodStartDate && !!firstPeriodEndDate && firstPeriodStartDate <= firstPeriodEndDate;
  let derivedDeadline: string | null = null;
  const warnings: string[] = [];
  if (firstPeriodEndDate && (!firstPeriodStartDate || validPeriod)) {
    try { derivedDeadline = taxDeadline(kind, firstPeriodEndDate); }
    catch { warnings.push('Period-end calculation is outside the supported date range; review manually.'); }
  }
  if (!firstPeriodEndDate) warnings.push('Actual tax period end is missing; registration/FY dates cannot supply it.');
  if (!firstPeriodStartDate) warnings.push('Actual tax period start is missing; automatic period recurrence is unavailable.');
  if (firstPeriodStartDate && firstPeriodEndDate && !validPeriod) warnings.push('Tax period boundaries are inverted; review required.');
  if (firstReturnDueDate && derivedDeadline && firstReturnDueDate !== derivedDeadline) warnings.push('Printed first due differs from the statutory period-end calculation; keep the reviewed printed date and check the discrepancy.');
  if (typeof extracted?.taxDateWarnings === 'string' && extracted.taxDateWarnings) warnings.push(extracted.taxDateWarnings);
  return {
    effectiveDate: taxDate(extracted?.effectiveDate), issueDate: taxDate(extracted?.issueDate),
    firstPeriodStartDate, firstPeriodEndDate, firstReturnDueDate,
    taxPeriodPattern: typeof extracted?.taxPeriodPattern === 'string' ? extracted.taxPeriodPattern.slice(0, 200) : null,
    deadline: firstReturnDueDate ?? derivedDeadline,
    deadlineSource: firstReturnDueDate ? 'PRINTED' as const : derivedDeadline ? 'DERIVED' as const : 'UNKNOWN' as const,
    derivedDeadline, warnings,
  };
}

export function taxPatternFrequency(kind: TaxKind, pattern: string | null): 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | null {
  if (!pattern) return null;
  const value = pattern.toLowerCase();
  if (kind === 'CT') {
    if (/annual|yearly|financial year|fiscal year|سنو/.test(value)) return 'ANNUAL';
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const range = value.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*[-–]\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/);
    return range && (months.indexOf(range[2]!) - months.indexOf(range[1]!) + 12) % 12 === 11 ? 'ANNUAL' : null;
  }
  if (/quarterly|ربع/.test(value) || ['jan', 'apr', 'jul', 'oct'].every((month) => value.includes(month))) return 'QUARTERLY';
  return /monthly|شهري/.test(value) ? 'MONTHLY' : null;
}

export function taxPeriodAnchorSupported(kind: TaxKind, pattern: string | null, start: string | null, end: string | null, frequency: string): boolean {
  if (!taxDate(start) || !taxDate(end) || start! > end! || taxPatternFrequency(kind, pattern) !== frequency) return false;
  const date = new Date(`${end}T00:00:00.000Z`);
  if (kind === 'VAT' && date.getUTCDate() !== new Date(taxUtc(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()) return false;
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const printedMonths = [...(pattern ?? '').matchAll(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b/gi)].map((match) => months.indexOf(match[1]!.toLowerCase()));
  if (kind === 'CT' && printedMonths.length === 2 && printedMonths[1] !== date.getUTCMonth()) return false;
  if (kind === 'VAT' && frequency === 'QUARTERLY' && printedMonths.length === 8 && !printedMonths.filter((_, index) => index % 2 === 1).includes(date.getUTCMonth())) return false;
  return true;
}

export const taxPeriodSchema = z.object({
  version: z.literal(1), kind: z.enum(['VAT', 'CT']), sourceDocumentId: idSchema,
  startDate: taxDateSchema, endDate: taxDateSchema, deadline: taxDateSchema,
  frequency: z.enum(['MONTHLY', 'QUARTERLY', 'ANNUAL']),
  deadlineSource: z.enum(['PRINTED', 'DERIVED']),
  endMonth: z.number().int().min(1).max(12), endDay: z.number().int().min(1).max(31),
}).strict().refine((value) => value.startDate <= value.endDate, 'Tax period start must not follow its end');
export type TaxPeriod = z.infer<typeof taxPeriodSchema>;

export function advanceTaxPeriod(period: TaxPeriod): TaxPeriod {
  const months = period.frequency === 'MONTHLY' ? 1 : period.frequency === 'QUARTERLY' ? 3 : 12;
  const end = new Date(`${period.endDate}T00:00:00.000Z`), start = new Date(end);
  start.setUTCDate(start.getUTCDate() + 1);
  let nextEnd: Date;
  if (period.kind === 'VAT') nextEnd = new Date(taxUtc(end.getUTCFullYear(), end.getUTCMonth() + months + 1, 0));
  else {
    const year = end.getUTCFullYear() + 1;
    nextEnd = new Date(taxUtc(year, period.endMonth - 1, Math.min(period.endDay, new Date(taxUtc(year, period.endMonth, 0)).getUTCDate())));
  }
  const endDate = nextEnd.toISOString().slice(0, 10);
  return taxPeriodSchema.parse({ ...period, startDate: start.toISOString().slice(0, 10), endDate, deadline: taxDeadline(period.kind, endDate), deadlineSource: 'DERIVED' });
}

export function taxPeriodLabel(period: TaxPeriod): { periodType: 'MONTH' | 'QUARTER' | 'YEAR'; periodValue: string } {
  const end = new Date(`${period.endDate}T00:00:00.000Z`);
  return period.frequency === 'MONTHLY' ? { periodType: 'MONTH', periodValue: period.endDate.slice(0, 7) }
    : period.frequency === 'QUARTERLY' ? { periodType: 'QUARTER', periodValue: `${end.getUTCFullYear()} Q${Math.floor(end.getUTCMonth() / 3) + 1}` }
      : { periodType: 'YEAR', periodValue: `FY ${period.endDate}` };
}
